import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { compact, isNil } from "lodash"

import config from "config"
import {
  get as getPersistValue,
  set as setPersistValue,
} from "utils/persist_store"
import {
  Q_SPLITR_CAMPAIGN_ID,
  Q_SPLITR_DEVICE_NAME,
  SPLITR_CAMPAIGN_ID,
  SPLITR_DEVICE_NAME,
} from "../constants"

export interface IUseSplitrPlayerProps {
  campaignId?: string | number
  deviceName?: string
}

export interface IUseSplitrPlayerResult {
  sourceURL: string
  campaignId: string | number
}

export type UseSplitrPlayer = (
  props?: IUseSplitrPlayerProps
) => IUseSplitrPlayerResult

export const useSplitrPlayer: UseSplitrPlayer = ({
  campaignId: campaignIdProp,
  deviceName: deviceNameProp,
}) => {
  const [searchParams] = useSearchParams()

  const campaignId = useMemo(() => {
    const currentCampaignId =
      searchParams.get(Q_SPLITR_CAMPAIGN_ID) || campaignIdProp
    const prevCampaignId = getPersistValue(SPLITR_CAMPAIGN_ID)

    if (isNil(currentCampaignId)) return prevCampaignId

    if (prevCampaignId !== currentCampaignId)
      setPersistValue(SPLITR_CAMPAIGN_ID, currentCampaignId)

    return currentCampaignId || prevCampaignId
  }, [searchParams, campaignIdProp])

  const deviceName = useMemo(() => {
    const currentDeviceName =
      searchParams.get(Q_SPLITR_DEVICE_NAME) || deviceNameProp
    const prevDeviceName = getPersistValue(SPLITR_DEVICE_NAME)

    if (isNil(currentDeviceName)) return prevDeviceName

    if (prevDeviceName !== currentDeviceName)
      setPersistValue(SPLITR_DEVICE_NAME, currentDeviceName)

    return currentDeviceName || prevDeviceName
  }, [searchParams, deviceNameProp])

  const sourceURL = useMemo(() => {
    const splitrHost = config("splitrHost")
    const queryString = deviceName ? `?device-name=${deviceName}` : ""
    return splitrHost
      ? `${compact([splitrHost, campaignId]).join("/")}${queryString}`
      : ""
  }, [campaignId, deviceName])

  return {
    sourceURL,
    campaignId,
    deviceName,
  }
}
